import React from "react";

import { Grid, Typography, colors, makeStyles } from "@material-ui/core";
import { uniq } from "lodash";

import PageHeader from "@ds-developer/page-header";

import { Brand, FranchiseType, PropositionAllocation } from "@hubcapp/types";
import { Error408 } from "@hubcapp/views";
import { useAuth } from "@hubcapp/context";
import { useQueryGetInstructorPropositionDetails } from "@hubcapp/hooks";

import { PageAccessManagementProps } from "./types";
import { shouldDisplayPage } from "./utils";

import LoadingScreen from "../LoadingScreen";

const useStyles = makeStyles(theme => ({
  root: {
    height: "100%",
    padding: theme.spacing(3, 2),
  },
  text: {
    width: "100%",
    marginTop: "48px",
    lineHeight: "24px",
    fontSize: "1rem",
    color: colors.blueGrey[600],

    "& a": {
      color: theme.palette.secondary.main,
    },
  },
}));

const PageAccessManagementStateProvider: React.FC<PageAccessManagementProps> = ({
  pageType,
  children,
}) => {
  const classes = useStyles();
  const {
    instructor: { id: instructorId },
  } = useAuth();

  const {
    data: instructorDetailsData,
    error,
    loading,
  } = useQueryGetInstructorPropositionDetails(instructorId);

  let propositionTypes: FranchiseType[] = [];
  let supportTelNumber = "0800 115533";

  if (instructorDetailsData?.instructor) {
    const { instructor } = instructorDetailsData;
    propositionTypes = uniq(
      instructor.propositions?.map(
        (propositionAllocation: PropositionAllocation) =>
          propositionAllocation?.proposition?.type,
      ),
    ) as FranchiseType[];

    const brands: Brand[] = uniq(
      instructor.propositions?.map(
        (propositionAllocation: PropositionAllocation) =>
          propositionAllocation?.proposition?.brand,
      ),
    );

    if (brands.every(brand => brand === Brand.Bsm)) {
      supportTelNumber = "0800 316 3658";
    }
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    // Pre-built error page that will allow refresh of page if an error occurs
    return <Error408 />;
  }

  if (shouldDisplayPage(pageType, propositionTypes)) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <Grid container className={classes.root} alignItems="center">
      <PageHeader title={`This feature isn’t available.`} />
      <Typography className={classes.text}>
        To view these pages, you need to have a branded franchise. <br />
        <br />
        If you are interested in this, please contact our Instructor Support
        team on{" "}
        <a href={`tel:${supportTelNumber.replace(/ /g, "")}`}>
          {supportTelNumber}
        </a>{" "}
        and select option 1
      </Typography>
    </Grid>
  );
};

export default PageAccessManagementStateProvider;
